import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, Box, Button, NativeSelect } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { UserContext } from "../shared/context/userContext";
import { lincolnFilters } from "./filterConfigs";
import EMPSelectField from "../shared/components/EMPSelectField/EMPSelectField";
import { useTranslation } from "react-i18next";
import { isCanadianPreorderUser } from "../utils/canadianPreorderExperience";

const OrdersSorting = ({
  disabled,
  totalOrders,
  onSortChange,
  sortValue,
  selectedFilters,
  onFilterRemove,
  onFilterClear,
  filtersDisabled = false,
}) => {
  const { user } = useContext(UserContext) || {};
  const { t } = useTranslation("emp");
  const isCanadianPreorder = isCanadianPreorderUser(user);
  if (user?.brand === "L") {
    const selections = Object.entries(selectedFilters || {}).flatMap(
      ([category, values]) =>
        values.map((value) => ({
          category,
          value,
          label:
            lincolnFilters[category]?.options.find(
              (option) => option.value === value
            )?.label || value,
        }))
    );
    return (
      <Box sx={{ mb: "12px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            minHeight: 32,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 1,
              flex: "1 1 0",
              minWidth: 0,
              height: 32,
              overflowX: "auto",
              overflowY: "hidden",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { height: "4px" },
            }}
          >
            {selections.map(({ category, value, label }) => (
              <Button
                key={`${category}-${value}`}
                variant="outlined"
                disabled={filtersDisabled}
                onClick={() => onFilterRemove(category, value)}
                aria-label={`Remove ${label} filter`}
                endIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                sx={{
                  borderColor: "#b2b2b2",
                  borderRadius: "4px",
                  px: 1.5,
                  py: 0,
                  minHeight: 24,
                  minWidth: 0,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  color: "#333",
                  fontSize: 13,
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                {label}
              </Button>
            ))}
            {selections.length > 0 && (
              <Button
                variant="outlined"
                disabled={filtersDisabled}
                onClick={onFilterClear}
                sx={{
                  borderColor: "#b2b2b2",
                  borderRadius: "4px",
                  px: 1.5,
                  py: 0,
                  minHeight: 24,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  color: "#333",
                  fontSize: 13,
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                Remove All
              </Button>
            )}
          </Box>
          <NativeSelect
            disableUnderline
            value={sortValue}
            disabled={disabled}
            onChange={(event) => onSortChange(event.target.value)}
            inputProps={{ "aria-label": "Sort orders" }}
            data-testid="sort-select"
            sx={{
              border: "1px solid #999",
              borderRadius: "6px",
              width: 172,
              flexShrink: 0,
              height: 32,
              px: "12px",
              fontSize: 16,
              fontFamily: "ProximaNovaRegular, Arial, sans-serif",
              color: "#444",
              bgcolor: "#fff",
              "& select": { pr: "24px !important" },
            }}
          >
            <option value="newest">Sort by: Newest</option>
            <option value="oldest">Sort by: Oldest</option>
            <option value="customerNameAsc">Last Name, A-Z</option>
            <option value="customerNameDesc">Last Name, Z-A</option>
          </NativeSelect>
        </Box>
        <Typography
          component="p"
          sx={{
            fontSize: 16,
            fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
            fontWeight: 600,
            lineHeight: "24px",
            letterSpacing: 0,
            mt: "8px",
          }}
        >
          Exact Matches ({totalOrders})
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Grid
        container
        justifyContent="flex-end"
        alignItems="center"
        sx={{ mb: 6 }}
      >
        <Grid item>
          <EMPSelectField
            testId="sort-select"
            inputProps={
              isCanadianPreorder
                ? {
                    "aria-label": t("CanadianPreorderOrders.sortLabel", {
                      defaultValue: "Sort orders",
                    }),
                  }
                : undefined
            }
            sx={isCanadianPreorder ? { maxWidth: 230, fontSize: 14 } : {}}
            menuItems={[
              ...(isCanadianPreorder
                ? [
                    {
                      text: t("CanadianPreorderOrders.sort.nearest", {
                        defaultValue: "Sort by: Nearest",
                      }),
                      value: "nearest",
                    },
                    {
                      text: t("CanadianPreorderOrders.sort.farthest", {
                        defaultValue: "Sort by: Farthest",
                      }),
                      value: "farthest",
                    },
                  ]
                : []),
              {
                text: isCanadianPreorder
                  ? t("CanadianPreorderOrders.sort.newest", {
                      defaultValue: "Sort by: Newest",
                    })
                  : "Sort by: Newest",
                value: "newest",
              },
              {
                text: isCanadianPreorder
                  ? t("CanadianPreorderOrders.sort.oldest", {
                      defaultValue: "Sort by: Oldest",
                    })
                  : "Sort by: Oldest",
                value: "oldest",
              },
              {
                text: isCanadianPreorder
                  ? t("CanadianPreorderOrders.sort.customerNameAsc", {
                      defaultValue: "Sort by: Last Name, A-Z",
                    })
                  : "Sort by: Last Name, A-Z",
                value: "customerNameAsc",
              },
              {
                text: isCanadianPreorder
                  ? t("CanadianPreorderOrders.sort.customerNameDesc", {
                      defaultValue: "Sort by: Last Name, Z-A",
                    })
                  : "Sort by: Last Name, Z-A",
                value: "customerNameDesc",
              },
            ]}
            disabled={disabled}
            value={sortValue}
            onChange={(event) => onSortChange(event.target.value)}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item>
          <Typography
            variant="h5"
            component="p"
            color="#4d4d4d"
            fontWeight={700}
          >
            {isCanadianPreorder
              ? t("CanadianPreorderOrders.exactMatches", {
                  defaultValue: "Exact Matches ({{count}})",
                  count: totalOrders,
                })
              : `Exact Matches (${totalOrders})`}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

OrdersSorting.propTypes = {
  filtersDisabled: PropTypes.bool,
  selectedFilters: PropTypes.object,
  onFilterRemove: PropTypes.func,
  onFilterClear: PropTypes.func,
  disabled: PropTypes.bool,
  onSortChange: PropTypes.func.isRequired,
  sortValue: PropTypes.string,
  totalOrders: PropTypes.number,
};

OrdersSorting.defaultProps = {
  disabled: false,
  sortValue: "newest",
  totalOrders: 0,
};

export default OrdersSorting;
